import {
    EVENTS_URL,
    IS_WEB_EVENT_BATCHING_ENABLED,
    SEND_UNAUTHORIZED_EVENTS_PATH,
} from "./config";
import { byteLength, fetchWithKeepaliveFallback, reserveKeepalive } from "./keepalive-budget";
import { KEEPALIVE_SENDER, trackKeepaliveRefusal } from "./keepalive-refusal";
import HTTP_CODES_ENUM from "./types/http-codes";
import { detectVercelInterstitial } from "./vercel-proxy";
import type { InterstitialKind } from "./vercel-proxy/types";
import { CLICK_EVENT_NAME } from "./web-event-beacon";

// Web events the page sends in batches instead of one request each. They only
// feed BigQuery. The API's batch route also still accepts `request`, which the
// page no longer sends.
const BATCHED_WEB_EVENT_NAMES = [CLICK_EVENT_NAME] as const;

export type BatchedWebEventName = (typeof BATCHED_WEB_EVENT_NAMES)[number];

/** Whether the page sends events of this name in batches. */
export function isBatchedWebEventName(name: unknown): name is BatchedWebEventName {
    // Widened to `unknown`, so `includes` takes a name of any type.
    const names: readonly unknown[] = BATCHED_WEB_EVENT_NAMES;
    return names.includes(name);
}

/** A batch goes out as soon as it holds this many events. */
const WEB_EVENT_BATCH_MAX_EVENTS = 10;

/** A batch goes out at the latest this many milliseconds after its first event. */
const WEB_EVENT_BATCH_MAX_WAIT_MS = 5000;

/**
 * A batch goes out before its body would pass this many bytes. That is half the
 * keepalive budget, so a batch in flight leaves room for the events that go on
 * their own. A web event is about 2.3 KB (median), so a full batch
 * holds about 6.
 */
const WEB_EVENT_BATCH_MAX_BYTES = 16 * 1024;

const WEB_EVENT_BATCH_URL = `${EVENTS_URL}${SEND_UNAUTHORIZED_EVENTS_PATH}`;

// ASCII, so its length is its size in bytes.
const EMPTY_BATCH_BYTES = JSON.stringify({ events: [] }).length;

// Each event goes out with how long it waited, so the API can date it back:
// `,"queued_ms":` and up to 8 digits (over a day; the API caps the wait at 24 h).
const QUEUED_MS_KEY = ",\"queued_ms\":";
const MAX_QUEUED_MS = 99_999_999;
const QUEUED_MS_BYTES = QUEUED_MS_KEY.length + String(MAX_QUEUED_MS).length;

// Answers that mean the API wrote nothing, so each event can go on its own
// without a duplicate: no batch route there yet (404), or a body it could not
// read (400). Like an edge page in `useFetchBase`, the backend never wrote it.
// The page then stops batching. A Vercel edge page (`detectVercelInterstitial`)
// also means the backend never saw the batch, so its events go on their own too,
// where a signed-in user's event gets the same-origin proxy retry of `fetchBase`;
// the page keeps batching. Any other failure is logged and not resent.
const SEND_ALONE_STATUSES: ReadonlySet<number> = new Set([HTTP_CODES_ENUM.BAD_REQUEST, HTTP_CODES_ENUM.NOT_FOUND]);

export type WebEventBatchFailure = {
    url: string;
    events: number;
    /** Whether the request that failed was sent with keepalive. */
    keepalive: boolean;
    /** Whether the batch asked for keepalive, even if it went again without. */
    keepaliveRequested: boolean;
} & (
    | {
          status: number;
          sentAlone: boolean;
          /** The Vercel edge page that answered instead of the API, if one did. */
          interstitial: InterstitialKind | null;
      }
    | { error: unknown }
);

export type QueuedWebEvent = {
    /** Sends the event on its own, the way an unbatched event goes. Must not throw. */
    sendAlone: () => void;
    /** Reports a batch that failed; called once per batch, on its first event. */
    onBatchFailed: (failure: WebEventBatchFailure) => void;
};

type QueueEntry = QueuedWebEvent & { body: string; queuedAt: number };

let queue: QueueEntry[] = [];
let queuedBytes = 0;
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let isListening = false;
let isPageHidden = false;
// While the page is visible, one batch at a time may hold keepalive budget, so
// batches never take more than half of it and the events that go on their own
// keep the rest. A batch sent as the page is hidden or left may hold it next to
// one still in flight: it carries the click that left the page.
let batchesHoldingKeepalive = 0;
// Set once the API itself answers a batch with 400 or 404 on this page.
let isBatchRouteRefused = false;

/** The body size of the queued events plus one more of `extraBytes`, comma included. */
const batchBytesWith = (extraBytes: number) =>
    EMPTY_BATCH_BYTES + queuedBytes + queue.length + extraBytes;

/** The event's JSON with `queued_ms` added as its last key. */
const withQueuedMs = ({ body, queuedAt }: QueueEntry, sentAt: number) =>
    `${body.slice(0, -1)}${QUEUED_MS_KEY}${Math.min(Math.max(Math.round(sentAt - queuedAt), 0), MAX_QUEUED_MS)}}`;

/** Whether this page sends click events in batches. */
function isWebEventBatchingEnabled(): boolean {
    return (
        IS_WEB_EVENT_BATCHING_ENABLED &&
        !isBatchRouteRefused &&
        typeof window !== "undefined" &&
        typeof fetch === "function"
    );
}

/**
 * Sends the queued events as one request. Its `text/plain` body is a
 * CORS-safelisted type and it carries no custom header, so the browser sends no
 * preflight. It claims the keepalive budget like any event, so it can outlive
 * the page; past the budget, or while the page is visible and another batch
 * holds keepalive, it goes without.
 */
export function flushWebEventBatch(): void {
    clearTimeout(flushTimer);
    flushTimer = undefined;
    if (queue.length === 0) return;

    const batch = queue;
    queue = [];
    queuedBytes = 0;

    // Events queued before the API refused an earlier batch go the way the rest now go.
    if (isBatchRouteRefused) {
        for (const entry of batch) entry.sendAlone();
        return;
    }

    const sentAt = Date.now();
    const body = `{"events":[${batch.map((entry) => withQueuedMs(entry, sentAt)).join(",")}]}`;
    const mayHoldKeepalive = batchesHoldingKeepalive === 0 || isPageHidden;
    const reservation = mayHoldKeepalive ? reserveKeepalive(body) : null;
    const keepalive = reservation !== null;
    if (reservation) batchesHoldingKeepalive += 1;
    let isReleased = false;
    const releaseKeepalive = () => {
        if (!reservation || isReleased) return;
        isReleased = true;
        reservation();
        batchesHoldingKeepalive -= 1;
    };
    // A refused keepalive request goes again without (`fetchWithKeepaliveFallback`)
    // and gives its room back at once.
    const refusal = trackKeepaliveRefusal({ sender: KEEPALIVE_SENDER.EVENT_BATCH, release: releaseKeepalive });
    const report = () => ({
        url: WEB_EVENT_BATCH_URL,
        events: batch.length,
        keepalive: keepalive && !refusal.wasRefused(),
        keepaliveRequested: keepalive,
    });
    const [first] = batch;

    // A wrapped `fetch` (an analytics script's) may throw instead of rejecting; an
    // async function turns that into a rejection. Its body runs at once, so the
    // request still starts inside the pagehide handler.
    const postBatch = async () =>
        fetchWithKeepaliveFallback(
            (init) => fetch(WEB_EVENT_BATCH_URL, init),
            {
                method: "POST",
                headers: { "Content-Type": "text/plain;charset=UTF-8" },
                body,
                keepalive,
            },
            refusal.onRefused
        );

    postBatch()
        .then(
            (response) => {
                // Before any resend, so the events sent on their own get the room back.
                releaseKeepalive();
                // A DDoS challenge answers 200, so this comes before `ok`.
                const interstitial = detectVercelInterstitial(response);
                const { ok, status } = response;
                if (ok && !interstitial) return;
                const isRefused = !interstitial && SEND_ALONE_STATUSES.has(status);
                if (isRefused) isBatchRouteRefused = true;
                const sentAlone = isRefused || interstitial !== null;
                if (sentAlone) {
                    for (const entry of batch) entry.sendAlone();
                }
                first?.onBatchFailed({ ...report(), status, sentAlone, interstitial });
            },
            (error: unknown) => {
                releaseKeepalive();
                first?.onBatchFailed({ ...report(), error });
            }
        );
}

// A hidden page may never run its timers again, and a closed one drops the
// queue, so both send it at once. While the page stays hidden, every new event
// goes out straight away.
function listenForPageExit(): void {
    if (isListening) return;
    isListening = true;
    isPageHidden = document.visibilityState === "hidden";

    document.addEventListener("visibilitychange", () => {
        isPageHidden = document.visibilityState === "hidden";
        if (isPageHidden) flushWebEventBatch();
    });
    window.addEventListener("pagehide", () => {
        isPageHidden = true;
        flushWebEventBatch();
    });
    window.addEventListener("pageshow", () => {
        isPageHidden = document.visibilityState === "hidden";
    });
}

/**
 * Queues a web event for the next batch. Returns `false` when the event must go
 * out on its own instead: batching is off, or the event alone is too big for a
 * batch (a click on a large element carries its text).
 */
export function queueWebEvent(event: unknown, handlers: QueuedWebEvent): boolean {
    if (!isWebEventBatchingEnabled()) return false;

    const body = JSON.stringify(event);
    // `withQueuedMs` appends a key to a JSON object that already has one.
    if (!body?.startsWith("{") || body === "{}") return false;
    const bytes = byteLength(body) + QUEUED_MS_BYTES;
    if (EMPTY_BATCH_BYTES + bytes > WEB_EVENT_BATCH_MAX_BYTES) return false;

    listenForPageExit();
    if (queue.length > 0 && batchBytesWith(bytes) > WEB_EVENT_BATCH_MAX_BYTES) {
        flushWebEventBatch();
    }

    // Wall-clock time, which keeps counting while the computer sleeps.
    queue.push({ ...handlers, body, queuedAt: Date.now() });
    queuedBytes += bytes;

    if (isPageHidden || queue.length >= WEB_EVENT_BATCH_MAX_EVENTS) {
        flushWebEventBatch();
    } else {
        flushTimer ??= setTimeout(flushWebEventBatch, WEB_EVENT_BATCH_MAX_WAIT_MS);
    }
    return true;
}
